/**
 * Renter-side character and schedule controls, surfaced inside the Console
 * (§7.1). These exist only when the listing granted `edit_character` or
 * `edit_schedule`; the Gate resolves that from the server capability set, so a
 * listing that never granted them renders nothing at all rather than a locked
 * panel.
 *
 * §7.8: the schedule editor is mobile-exempt. On a phone it shows a clear
 * message instead of a broken layout — but it never hides the fact that the
 * capability exists.
 */

import { useState } from 'react';
import { useApi, useMutation } from '../../hooks/useApi';
import type { Bot, CharacterProfile, Rental, Schedule, Page } from '../../api/types';
import type { CapabilityResolver } from '../../hooks/useCapabilities';
import { useLayout } from '../../hooks/useMediaQuery';
import { Gate } from '../../components/Gate';
import { Card, Field, LiveRegion } from '../../components/ui';
import { ErrorView } from '../../components/ErrorView';
import { BotTime } from '../../components/TimeDisplay';
import { routes } from '../../api/routes';

export function RenterCharacterPanel({
  bot,
  caps,
  rental,
}: {
  bot: Bot;
  caps: CapabilityResolver;
  rental: Rental | null;
}) {
  const current = useApi<CharacterProfile>(
    caps.visible('edit_character') || caps.visible('view_character') ? routes.bots.character(bot.id) : null,
  );
  const [tone, setTone] = useState<string | null>(null);
  const propose = useMutation<Record<string, unknown>, unknown>(routes.bots.character(bot.id), {
    method: 'PATCH',
  });

  if (!caps.visible('view_character') && !caps.visible('edit_character')) return null;

  const profile = current.data;
  const value = tone ?? profile?.tone ?? '';

  return (
    <Card title="Character">
      {current.problem ? <ErrorView problem={current.problem} onRetry={current.reload} /> : null}
      {profile ? (
        <>
          <p className="muted">Version {profile.version}</p>
          <p className="preview">{profile.description}</p>
        </>
      ) : null}

      <Gate caps={caps} capability="edit_character">
        {(disabled) => (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void propose.run({ tone: value });
            }}
          >
            <fieldset disabled={disabled}>
              <Field
                id="renter-tone"
                label="Tone"
                hint={
                  rental
                    ? 'A change during a rental is sent to the owner for consent before it takes effect.'
                    : undefined
                }
              >
                <textarea
                  id="renter-tone"
                  className="textarea"
                  rows={3}
                  value={value}
                  onChange={(e) => setTone(e.target.value)}
                />
              </Field>
              <button type="submit" className="btn btn-primary" disabled={disabled || propose.pending}>
                {propose.pending ? 'Sending…' : 'Propose this change'}
              </button>
            </fieldset>
            {propose.problem ? <ErrorView problem={propose.problem} /> : null}
            {propose.data ? <LiveRegion>Sent to the owner for consent.</LiveRegion> : null}
          </form>
        )}
      </Gate>
    </Card>
  );
}

export function RenterSchedulePanel({ bot, caps }: { bot: Bot; caps: CapabilityResolver }) {
  const layout = useLayout();
  const list = useApi<Page<Schedule>>(
    caps.visible('edit_schedule') ? routes.bots.schedules(bot.id) : null,
  );
  const toggle = useMutation<{ id: string; enabled: boolean }, Schedule>(
    (args) => routes.bots.schedule(bot.id, args.id),
    { method: 'PATCH' },
  );

  return (
    <Gate caps={caps} capability="edit_schedule">
      {(disabled) => (
        <Card title="Schedule">
          {layout === 'compact' ? (
            <p className="desktop-only-note">
              The schedule editor needs a wider screen. Open this console on a laptop or desktop to
              change the schedule; everything else here works on a phone.
            </p>
          ) : (
            <>
              {list.problem ? <ErrorView problem={list.problem} onRetry={list.reload} /> : null}
              <ul className="schedule-list">
                {(list.data?.data ?? []).map((s) => (
                  <li key={s.id} className="schedule-item">
                    <div>
                      <p>{s.rrule_plain}</p>
                      <p className="muted">
                        Next: <BotTime iso={s.next_fire_at} timeZone={s.timezone} />
                      </p>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm"
                      disabled={disabled || toggle.pending}
                      onClick={() => void toggle.run({ id: s.id, enabled: !s.enabled })}
                    >
                      {s.enabled ? 'Disable' : 'Enable'}
                    </button>
                  </li>
                ))}
              </ul>
              {(list.data?.data ?? []).length === 0 ? (
                <p className="muted">This bot has no schedules.</p>
              ) : null}
            </>
          )}
        </Card>
      )}
    </Gate>
  );
}
